import * as React from "react";

type NeonVariant = "upload" | "processing" | "primary" | "success";

export interface NeonProgressProps {
  value: number;             // 0..100
  variant?: NeonVariant;     // Color variant
  label?: string;            // a11y label
  className?: string;
  showPercentage?: boolean;  // Show percentage text
  animated?: boolean;        // Enable glow animation
}

/** Barra neon alinhada ao tema arcade (tokens retro — sem purple genérico). */
export function NeonProgress({
  value,
  variant = "primary",
  label = "Progress",
  className = "",
  showPercentage = true,
  animated = true,
}: NeonProgressProps) {
  const safe = Math.max(0, Math.min(100, Math.round(value)));
  const isComplete = safe >= 100;

  const variantStyles = {
    upload: {
      background: 'linear-gradient(90deg, #00f5ff, #5cf2d6)',
      shadow: '0 0 16px #00f5ff88, 0 0 28px #5cf2d644',
      glow: '#00f5ff',
    },
    processing: {
      background: 'linear-gradient(90deg, #ff6b35, #ffcc66)',
      shadow: '0 0 16px #ff6b3588, 0 0 28px #ffcc6644',
      glow: '#ff6b35',
    },
    primary: {
      background: 'linear-gradient(90deg, #5cf2d6, #00ff41)',
      shadow: '0 0 16px #5cf2d688, 0 0 28px #00ff4144',
      glow: '#5cf2d6',
    },
    success: {
      background: 'linear-gradient(90deg, #5ee37d, #34d399)',
      shadow: '0 0 16px #5ee37d88, 0 0 28px #34d39944',
      glow: '#5ee37d',
    },
  };

  const currentStyle = variantStyles[variant];
  const [reduceMotion, setReduceMotion] = React.useState(false);
  React.useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReduceMotion(mq.matches);
    const onChange = () => setReduceMotion(mq.matches);
    mq.addEventListener?.('change', onChange);
    return () => mq.removeEventListener?.('change', onChange);
  }, []);
  const doAnimate = animated && !reduceMotion;

  return (
    <div className={`relative w-full ${className}`} role="group" aria-label={label}>
      <div className="relative h-5 bg-[var(--surface-2)] rounded-retro border-2 border-[var(--line-soft)] overflow-hidden">
        <div
          className="absolute inset-0 opacity-20"
          style={{
            background: `radial-gradient(ellipse at center, ${currentStyle.glow}20 0%, transparent 70%)`,
          }}
        />

        <div
          className={`absolute top-0 left-0 h-full rounded-sm transition-all duration-300 ease-out ${
            doAnimate ? 'animate-pulse' : ''
          }`}
          style={{
            width: `${safe}%`,
            background: currentStyle.background,
            boxShadow: doAnimate && safe > 0 ? currentStyle.shadow : 'none',
          }}
        >
          <div
            className="absolute top-0 left-0 h-1/2 w-full rounded-full opacity-40"
            style={{
              background: 'linear-gradient(180deg, rgba(255,255,255,0.5) 0%, transparent 100%)',
            }}
          />
        </div>
      </div>

      {showPercentage && (
        <div className="flex justify-between items-center mt-1.5 text-xs font-mono">
          <span className="text-[var(--muted)] uppercase tracking-wide">
            {variant}
          </span>
          <span
            className={`font-bold ${isComplete && doAnimate ? 'animate-pulse' : ''}`}
            style={{
              color: currentStyle.glow,
              textShadow: doAnimate ? `0 0 8px ${currentStyle.glow}` : 'none',
            }}
          >
            {safe}%
          </span>
        </div>
      )}

      <span className="sr-only">{safe}% {label}</span>
    </div>
  );
}

export default NeonProgress;
